import React from 'react';
import { Button, Input } from '@sisumail/ui';
import {
    ChevronLeft,
    ChevronRight,
    Search as SearchIcon,
    Trash2,
    Zap,
} from 'lucide-react';
import { Badge } from './Badge';
import type { Identity } from '../../hooks/useAdminData';

interface IdentitiesTableProps {
    identities: Identity[];
    total: number;
    page: number;
    limit: number;
    search: string;
    onSearchChange: (value: string) => void;
    onPageChange: (newPage: number) => void;
    onActivate: (username: string) => void;
    onRevoke: (username: string) => void;
}

export function IdentitiesTable({
    identities,
    total,
    page,
    limit,
    search,
    onSearchChange,
    onPageChange,
    onActivate,
    onRevoke,
}: IdentitiesTableProps) {
    return (
        <div className="space-y-4">
            <div className="flex gap-4">
                <Input
                    placeholder="Search username..."
                    value={search}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onSearchChange(e.target.value);
                    }}
                    className="flex-1"
                    suffix={<SearchIcon size={16} />}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => onPageChange(Math.max(0, page - 1))}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="min-w-[100px] text-center font-bold text-neutral-500 text-xs uppercase">
                        Page {page + 1} of {Math.ceil(total / limit) || 1}
                    </span>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={(page + 1) * limit >= total}
                        onClick={() => onPageChange(page + 1)}
                    >
                        <ChevronRight size={16} />
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-800 bg-gray-950 shadow">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-900 text-gray-400 uppercase">
                        <tr>
                            <th className="px-6 py-4">Username</th>
                            <th className="px-6 py-4">State</th>
                            <th className="px-6 py-4">Created</th>
                            <th className="px-6 py-4">Expires</th>
                            <th className="px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {identities.map((id) => (
                            <tr key={id.username} className="hover:bg-gray-900/50">
                                <td className="px-6 py-4 font-medium font-mono text-white">
                                    {id.username}
                                </td>
                                <td className="px-6 py-4">
                                    <Badge state={id.state} />
                                </td>
                                <td className="px-6 py-4 text-gray-400">
                                    {new Date(id.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 text-gray-400">
                                    {id.expires_at
                                        ? new Date(id.expires_at).toLocaleDateString()
                                        : '-'}
                                </td>
                                <td className="flex gap-2 px-6 py-4">
                                    {id.state !== 'ACTIVE' && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onActivate(id.username)}
                                            className="text-emerald-500"
                                            title="Activate"
                                        >
                                            <Zap size={16} />
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onRevoke(id.username)}
                                        className="text-red-500"
                                        title="Revoke"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {identities.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="p-8 text-center text-gray-500"
                                >
                                    No identities found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
